import { markupInks } from '@munifocus/ui';
import type { MarkupAnnotation } from '@munifocus/contracts-gov';

/**
 * Draw annotations onto a 2D canvas the size of the displayed photo. This is
 * the editor's preview only: the copy that goes on the record is rendered by
 * gov-api from the original bytes and the same annotation list, so what is
 * drawn here never becomes evidence by itself.
 */
export function drawAnnotations(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  annotations: MarkupAnnotation[],
): void {
  ctx.clearRect(0, 0, width, height);
  const stroke = Math.max(2, Math.round(Math.max(width, height) / 250));
  const fontSize = Math.max(13, Math.round(Math.max(width, height) / 45));
  const at = ([x, y]: [number, number]) => [x * width, y * height] as const;

  const label = (text: string, x: number, y: number, colour: MarkupAnnotation['colour']) => {
    ctx.font = `700 ${fontSize}px sans-serif`;
    const w = ctx.measureText(text).width;
    const pad = fontSize * 0.3;
    const light = colour === 'yellow' || colour === 'white';
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = light ? markupInks.black : markupInks.white;
    ctx.fillRect(x, y - fontSize - pad, w + pad * 2, fontSize + pad * 2);
    ctx.globalAlpha = 1;
    ctx.fillStyle = markupInks[colour];
    ctx.fillText(text, x + pad, y - pad * 0.2);
  };

  for (const a of annotations) {
    const ink = markupInks[a.colour];
    ctx.strokeStyle = ink;
    ctx.fillStyle = ink;
    ctx.lineWidth = stroke;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    switch (a.kind) {
      case 'arrow': {
        const [x1, y1] = at(a.points[0]);
        const [x2, y2] = at(a.points[1]);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const len = stroke * 5;
        for (const side of [-1, 1]) {
          const theta = angle + Math.PI - side * (Math.PI / 7);
          ctx.moveTo(x2, y2);
          ctx.lineTo(x2 + len * Math.cos(theta), y2 + len * Math.sin(theta));
        }
        ctx.stroke();
        break;
      }
      case 'ellipse':
      case 'rect': {
        const [x1, y1] = at(a.points[0]);
        const [x2, y2] = at(a.points[1]);
        const x = Math.min(x1, x2);
        const y = Math.min(y1, y2);
        const w = Math.abs(x2 - x1);
        const h = Math.abs(y2 - y1);
        ctx.beginPath();
        if (a.kind === 'rect') ctx.rect(x, y, w, h);
        else
          ctx.ellipse(
            x + w / 2,
            y + h / 2,
            Math.max(w / 2, 1),
            Math.max(h / 2, 1),
            0,
            0,
            Math.PI * 2,
          );
        ctx.stroke();
        break;
      }
      case 'freehand': {
        ctx.beginPath();
        a.points.forEach((p, i) => {
          const [x, y] = at(p);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
        break;
      }
      case 'marker': {
        const [x, y] = at(a.points[0]);
        ctx.beginPath();
        ctx.arc(x, y, stroke * 4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, y, stroke, 0, Math.PI * 2);
        ctx.fill();
        if (a.text) label(a.text, x + stroke * 5, y + fontSize / 2, a.colour);
        break;
      }
      case 'text': {
        const [x, y] = at(a.points[0]);
        label(a.text ?? '', x, y + fontSize, a.colour);
        break;
      }
    }
  }
}

/** Pointer position as a 0–1 fraction of the element, clamped. */
export function normalisePoint(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
): [number, number] {
  const clamp = (n: number) => Math.min(1, Math.max(0, n));
  const round = (n: number) => Math.round(n * 10000) / 10000;
  return [
    round(clamp((clientX - rect.left) / (rect.width || 1))),
    round(clamp((clientY - rect.top) / (rect.height || 1))),
  ];
}
